import { useState, useEffect, useContext } from "react";
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaSync,
  FaFilePdf,
  FaFileCsv,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
} from "react-icons/fa";
import AuthContext from "../../contexts/AuthContext";
import {
  getMainStoreData,
  getMainStoreSuppliers,
} from "../../utils/inventoryApi";
import MainStorePDF from "../../components/inventory/MainStorePDF";

export default function MainStore() {
  const { restaurantId } = useContext(AuthContext);
  const [mainStoreData, setMainStoreData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [stockLevel, setStockLevel] = useState("");
  const [sortBy, setSortBy] = useState("prodName");

  useEffect(() => {
    if (restaurantId) {
      fetchMainStoreData();
      fetchSuppliers();
    }
  }, [restaurantId]);

  async function fetchMainStoreData() {
    setLoading(true);
    setError("");
    try {
      const data = await getMainStoreData(restaurantId);
      setMainStoreData(data || []);
    } catch (err) {
      console.error("Failed to fetch main store data:", err);
      setError("Failed to fetch main store data");
    }
    setLoading(false);
  }

  async function handleRefresh() {
    // Refresh data without clearing filters
    await fetchMainStoreData();
    await fetchSuppliers();
  }

  async function fetchSuppliers() {
    try {
      const data = await getMainStoreSuppliers(restaurantId);
      setSuppliers(data || []);
    } catch (err) {
      console.error("Failed to fetch suppliers:", err);
    }
  }

  // Apply filters to data
  const filteredData = mainStoreData.filter((item) => {
    // Search filter
    const searchMatch =
      !searchTerm ||
      item.prodName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.prodId?.includes(searchTerm);

    // Supplier filter
    const supplierMatch =
      !selectedSupplier ||
      item.suppliers?.some((supplier) => supplier === selectedSupplier);

    // Date range filter
    const dateMatch =
      !startDate ||
      !endDate ||
      (item.lastPurchaseDate &&
        item.lastPurchaseDate >= startDate &&
        item.lastPurchaseDate <= endDate);

    // Stock level filter
    const stockMatch =
      !stockLevel ||
      (stockLevel === "Low Stock" && item.totalQty < 10) ||
      (stockLevel === "In Stock" &&
        item.totalQty >= 10 &&
        item.totalQty <= 100) ||
      (stockLevel === "Over Stock" && item.totalQty > 100);

    return searchMatch && supplierMatch && dateMatch && stockMatch;
  });

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    switch (sortBy) {
      case "prodName":
        return (a.prodName || "").localeCompare(b.prodName || "");
      case "totalQty":
        return b.totalQty - a.totalQty;
      case "totalValue":
        return b.totalValue - a.totalValue;
      case "lastUpdated":
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      default:
        return 0;
    }
  });

  // Get items to display (first 10 or all based on showAllItems)
  const itemsToDisplay = showAllItems ? sortedData : sortedData.slice(0, 10);
  const hasMoreItems = sortedData.length > 10;

  // Calculate summary statistics
  const totalProducts = sortedData.length;
  const totalValue = sortedData.reduce(
    (sum, item) => sum + (item.totalValue || 0),
    0
  );
  const lowStockCount = sortedData.filter((item) => item.totalQty < 10).length;
  const activeSuppliersCount = new Set(
    sortedData.flatMap((item) => item.suppliers || [])
  ).size;

  function getStockStatus(item) {
    if (item.totalQty < 10) {
      return { text: "Low Stock", class: "bg-red-100 text-red-800" };
    } else if (item.totalQty > 100) {
      return { text: "Over Stock", class: "bg-yellow-100 text-yellow-800" };
    } else {
      return { text: "In Stock", class: "bg-green-100 text-green-800" };
    }
  }

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  }

  function handleExportPDF() {
    console.log("Export PDF clicked");
    console.log("sortedData:", sortedData);
    console.log("currentFilters:", currentFilters);
    setShowPDFModal(true);
  }

  function handleExportCSV() {
    // Prepare CSV data
    const headers = [
      "SNo",
      "Product ID",
      "Product Name",
      "UOM",
      "Opening Stock",
      "Purchased Qty",
      "Total Qty",
      "Avg Rate",
      "Total Value",
      "Suppliers",
      "Last Purchase",
      "Status",
    ];

    const csvData = sortedData.map((item, index) => {
      const status = getStockStatus(item);
      return [
        index + 1,
        item.prodId,
        item.prodName,
        item.uom,
        item.openingStockQty,
        item.inventoryQty,
        item.totalQty,
        item.avgRate?.toFixed(2) || "0.00",
        item.totalValue?.toLocaleString() || "0",
        item.suppliers?.join(", ") || "N/A",
        formatDate(item.lastPurchaseDate),
        status.text,
      ];
    });

    // Add headers to the beginning
    const csvContent = [headers, ...csvData];

    // Convert to CSV string
    const csvString = csvContent
      .map((row) =>
        row
          .map((cell) =>
            typeof cell === "string" && cell.includes(",") ? `"${cell}"` : cell
          )
          .join(",")
      )
      .join("\n");

    // Create and download CSV file
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `main-store-data-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Prepare filters object for PDF modal
  const currentFilters = {
    searchTerm,
    selectedSupplier,
    startDate,
    endDate,
    stockLevel,
    sortBy,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">
            Main Store
          </h1>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <button
            onClick={handleExportPDF}
            className="bg-red-500 hover:bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm"
          >
            <FaFilePdf />
            <span className="hidden sm:inline">Export PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>
          <button
            onClick={handleExportCSV}
            className="bg-green-500 hover:bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm"
          >
            <FaFileCsv />
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">CSV</span>
          </button>
          <button
            onClick={handleRefresh}
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm"
          >
            <FaSync />
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">Refresh</span>
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Search Products
            </label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Supplier
            </label>
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            >
              <option value="">All Suppliers</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.name}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              From Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              To Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Stock Level
            </label>
            <select
              value={stockLevel}
              onChange={(e) => setStockLevel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            >
              <option value="">All Levels</option>
              <option value="Low Stock">Low Stock</option>
              <option value="In Stock">In Stock</option>
              <option value="Over Stock">Over Stock</option>
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            >
              <option value="prodName">Product Name</option>
              <option value="totalQty">Total Quantity</option>
              <option value="totalValue">Total Value</option>
              <option value="lastUpdated">Last Updated</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 sm:p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-blue-600 dark:text-blue-400">
                Total Products
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-800 dark:text-blue-200">
                {totalProducts}
              </p>
            </div>
            <FaCheckCircle className="text-blue-500 text-lg sm:text-xl lg:text-2xl" />
          </div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-3 sm:p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-green-600 dark:text-green-400">
                Total Value
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-800 dark:text-green-200">
                ₹{totalValue.toLocaleString()}
              </p>
            </div>
            <FaCheckCircle className="text-green-500 text-lg sm:text-xl lg:text-2xl" />
          </div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 sm:p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-yellow-600 dark:text-yellow-400">
                Low Stock Items
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-yellow-800 dark:text-yellow-200">
                {lowStockCount}
              </p>
            </div>
            <FaExclamationTriangle className="text-yellow-500 text-lg sm:text-xl lg:text-2xl" />
          </div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 sm:p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-purple-600 dark:text-purple-400">
                Active Suppliers
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-800 dark:text-purple-200">
                {activeSuppliersCount}
              </p>
            </div>
            <FaClock className="text-purple-500 text-lg sm:text-xl lg:text-2xl" />
          </div>
        </div>
      </div>

      {/* Main Store Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
            <thead className="bg-gray-50 dark:bg-slate-700">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  SNo
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Prod ID
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Product Name
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  UOM
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Opening Stock
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Purchased Qty
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Total Qty
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Avg Rate
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Total Value
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Suppliers
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Last Purchase
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
              {sortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={12}
                    className="px-3 sm:px-6 py-4 text-center text-gray-500 dark:text-gray-400 text-sm"
                  >
                    {error ? error : "No main store items found."}
                  </td>
                </tr>
              ) : (
                <>
                  {itemsToDisplay.map((item, idx) => {
                    const status = getStockStatus(item);
                    return (
                      <tr
                        key={item.prodId}
                        className="hover:bg-gray-50 dark:hover:bg-slate-700"
                      >
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                          {idx + 1}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                          {item.prodId}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                          {item.prodName}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                          {item.uom}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                          {item.openingStockQty}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                          {item.inventoryQty}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                          {item.totalQty}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                          ₹{item.avgRate?.toFixed(2) || "0.00"}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                          ₹{item.totalValue?.toLocaleString() || "0"}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                          {item.suppliers?.join(", ") || "N/A"}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                          {formatDate(item.lastPurchaseDate)}
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${status.class}`}
                          >
                            {status.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })}

                  {/* Total Row */}
                  <tr className="bg-gray-100 dark:bg-slate-600 border-t-2 border-gray-300 dark:border-slate-500">
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      -
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      -
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      TOTAL
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      -
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      {itemsToDisplay
                        .reduce(
                          (sum, item) => sum + (item.openingStockQty || 0),
                          0
                        )
                        .toLocaleString()}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      {itemsToDisplay
                        .reduce(
                          (sum, item) => sum + (item.inventoryQty || 0),
                          0
                        )
                        .toLocaleString()}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      {itemsToDisplay
                        .reduce((sum, item) => sum + (item.totalQty || 0), 0)
                        .toLocaleString()}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      -
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-red-600 dark:text-red-400">
                      ₹
                      {itemsToDisplay
                        .reduce((sum, item) => sum + (item.totalValue || 0), 0)
                        .toLocaleString()}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      -
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      -
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                      -
                    </td>
                  </tr>
                  {hasMoreItems && (
                    <tr>
                      <td colSpan={12} className="text-center py-2">
                        <button
                          className="text-blue-600 hover:underline text-sm"
                          onClick={() => setShowAllItems(!showAllItems)}
                        >
                          {showAllItems ? "Read Less" : "Read More"}
                        </button>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PDF Modal */}
      <MainStorePDF
        isOpen={showPDFModal}
        onClose={() => setShowPDFModal(false)}
        data={sortedData}
        filters={currentFilters}
      />
    </div>
  );
}
